// react core
import { useContext } from "react";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// entzy resources
import configEntzy from "components/config/ConfigEntzy";
import InputForm from "components/input/InputForm";
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import { createEventStructureConstants } from "models/event";
import { idGenerator } from "services/tools";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAtomAlt as iconEventuators } from "@fortawesome/pro-thin-svg-icons";
import { faClone as iconClone } from "@fortawesome/pro-duotone-svg-icons";

function ActionLaunchpadRunnerClone(props) {
  const user = props.user;
  const navigate = props.navigate;

  const section = "members";
  const anchorTop = "anchor-launchpad-edit-" + section + "-top";

  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);

  const eventConstants = createEventStructureConstants();

  const handleClose = () => {
    mainContext.setActionDrawer({
      open: false,
      params: {},
    });
    mainContext.updateViewerTrail({
      showActionSettings: false,
    });
  };

  const handleCreateCallback = async (data, fastCreate) => {
    const eventName = data
      .filter((obj) => obj.id === "event-name")[0]
      .value.toLowerCase();
    const eventCountry = data.filter((obj) => obj.id === "event-country")[0]
      .value;
    const eventCurrency = data.filter((obj) => obj.id === "event-currency")[0]
      .value;

    if (eventName && eventName.length && eventCountry && eventCurrency) {
      const eventContent = await eventContext.prepareCreateEvent({
        eventId: eventContext.state.event.data.EventId,
        name: eventName,
        country: eventCountry.code.short,
        currency: eventCurrency.code.long,
        clone: true,
      });
      if (eventContent.alert) {
        return {
          alert: true,
          message: eventContent.message,
        };
      } else {
        let response, errors;
        errors = false;

        // track source and target values for cloning
        // const sourceEventId = eventContext.state.event.data.EventId;
        const clonedEventId = eventContent.data.EventId;
        const sourceUrl = eventContext.state.event.data.Url;
        const clonedUrl = eventContent.data.Url;
        const sourceCountry = eventContext.state.event.data.Country;
        const clonedCountry = eventContent.data.Country;
        // const sourceCurrency = eventContext.state.event.data.Currency;
        // const clonedCurrency = eventContent.data.Currency;

        // clone runner locations
        // only clone if source country is same
        // or if a global selection "e" or "ww" worldwide
        const locationData = [];
        if (
          sourceCountry === clonedCountry ||
          clonedCountry === "e" ||
          clonedCountry === "ww"
        ) {
          eventContext.state.places.data.runners.forEach((place, index) => {
            locationData.push({
              id: "new-" + idGenerator(),
              order: index + 1,
              active: place.LinkStatus === "active",
              coordinates: place.LinkTarget,
              description: place.Description,
              name: place.Title,
              subtag: "runner",
              tag: "Location",
              user: user.identity,
            });
          });
          response = await eventContext.prepareUpdatePlaces({
            EventId: clonedEventId,
            key: "Locations",
            value: locationData,
            category: "runner",
          });
          if (response.alert) {
            // flag alert if any problems cloning places
            // but do not stop overall clone process
            mainContext.updateAlert(response);
            errors = true;
          }
          eventContext.updatePlaces(response);
        }

        // clone products from eventuator store
        const productData = [];
        response = await mainContext.preparePullProductList({
          Url: sourceUrl,
          List: configEntzy.PRODUCT_LIST_DEFAULT,
          Category: configEntzy.PRODUCT_CATEGORY_DEFAULT,
          limit: 1000,
        });
        if (response.success) {
          response.data.items.forEach((product) => {
            // no need to filter as products can be in any currency so copy as is
            // if (product.Currency === sourceCurrency) productData.push(product);
            productData.push(product);
          });
        }
        response = await Promise.all(
          productData.map(async (product) => {
            return await mainContext.prepareCreateProduct({
              Url: clonedUrl,
              List: product.List,
              Category: product.Category,
              Currency: product.Currency,
              Amount: product.Amount,
              Description: product.Description,
            });
          }),
        );
        for (let r of response) {
          if (r.alert) {
            // flag alert if any problems cloning products
            // but do not stop overall clone process
            mainContext.updateAlert(r);
            errors = true;
          } else {
            mainContext.createProduct(r.data);
          }
        }

        // update main context for change in event list
        mainContext.addEvent(eventContent.data, true, true);

        // start new event with cloned data and navigate to it
        const startData = await eventContext.prepareStartEvent(
          eventContent.data,
        );
        const startPayload = {
          startEvent: true,
          params: [eventContent, startData, "create"],
        };
        handleClose();
        navigate(eventContent.urlComponents.relative, {
          state: startPayload,
        });

        // if no errors activate loader and after short delay reload page
        // this ensures all data is freshly pulled into new eventuator context
        if (!errors) {
          mainContext.setCallbackLoader(true);
          setTimeout(() => {
            window.location.reload();
            mainContext.setCallbackLoader(false);
          }, 3000);
        }

        return { alert: false, data: eventContent.data };
      }
    } else {
      return {
        alert: true,
        message: fastCreate
          ? "Eventuator URL is required"
          : "Eventuator URL, launch country and ticket currency required",
      };
    }
  };

  return (
    <Box
      id={anchorTop}
      className="box-default"
      sx={{
        pt: configEntzy.APP_SPACING_MD,
        pb: configEntzy.APP_SPACING_MD,
        mb: configEntzy.APP_SPACING_XL,
      }}
    >
      <Box className="box-default">
        <Typography variant="subtitle2">
          <span className="fa-layers fa-fw fa-3x">
            <FontAwesomeIcon
              icon={iconClone}
              transform="up-8 left-10"
              style={{
                color: "rgb(255, 243, 230)",
              }}
            />
            <FontAwesomeIcon
              icon={iconEventuators}
              transform="up-8 right-10"
              color="orange"
            />
          </span>
        </Typography>
        <Typography variant="h6" color="primary">
          Clone Eventuator
        </Typography>
        <Typography variant="subtitle1" color="white">
          <em>Create a copy of this eventuator</em>
        </Typography>
      </Box>

      <Box className="box-default">
        <Box
          className="box-default"
          sx={{
            pt: configEntzy.APP_SPACING_MD2X,
            pb: configEntzy.APP_SPACING_MD3X,
          }}
        >
          <Typography variant="subtitle2">
            Cloning will copy all core settings from this eventuator into a new
            one with a fresh calendar. If you change the currency it will reset
            any offer type prices to zero, so you can update them after
            creation.
          </Typography>
        </Box>
        <Container maxWidth="xs">
          <InputForm
            navigate={props.navigate}
            fields={[
              {
                id: "event-country",
                type: "selector-country",
                label: "Country where launches will be held",
                value: null,
                required: true,
              },
              {
                id: "event-currency",
                type: "selector-currency",
                label: "Currency for any transactions",
                value: null,
                required: true,
              },
              {
                id: "event-name",
                type: "text-standard",
                label: "Eventuator Name",
                value: "",
                alphanumeric: true,
                required: true,
                maxLength: eventConstants.maxNameLength,
              },
            ]}
            submitText="Create"
            callback={handleCreateCallback}
            // cancel={handleShowCreateForm}
            // cancelText="Cancel"
            dynamicPreviewText={{
              header: "Your Eventuator URL:",
              values: [
                { type: "fixed", value: configEntzy.APP_URL },
                { type: "fixed", value: "/" },
                {
                  type: "field",
                  id: "event-country",
                  key: "code",
                  subKey: "short",
                },
                { type: "fixed", value: "/" },
                { type: "field", id: "event-name" },
              ],
            }}
            footNotes={[
              "These core settings cannot be changed after creation.",
              "Select worldwide if your launches will be multi-country.",
            ]}
          />
        </Container>
      </Box>
    </Box>
  );
}

export default ActionLaunchpadRunnerClone;
